import { cloneDeep } from 'lodash';
import { v4 as uuid } from 'uuid';
import { type IFramework } from '@/framework';
import type { Layer } from '@/layers/layer';
import { traverseTree, traverseTrees } from '@/utils/tree-kit';
import { parseServerLayersToClient, parseServerToClientMap, type IServerGroupLayerProps } from '@/version-parse';
import {
    DEFAULT_FONT_FAMILY,
    LayerType,
    StretchOptionType,
    type IGroupLayerProps,
    type IImageLayerProps,
    type ILayerProps,
    type ITextLayerProps,
    type IVectorLayerProps,
} from '@/version-types';

export interface ITextMaterial {
    /**
     * 将素材图层存储到缓存中
     * @param materialID - 素材 ID
     * @param serveLayer - 服务器端的图层数据
     * @param version - 可选，素材版本号
     */
    storeMaterialLayerToCache(materialID: string, serveLayer: IServerGroupLayerProps, version?: string): ILayerProps;

    /**
     * 从缓存中加载素材图层
     * @param materialID - 素材 ID
     * @returns - 服务器端的图层数据
     */
    loadMaterialLayerFromCache(materialID: string): ILayerProps | null;

    /**
     * 添加围绕文本创建的素材图层
     * @param layer - 素材图层
     * @param addFn - 添加函数
     * @returns
     */
    addByMaterialLayer(layer: ILayerProps, byMousePosition?: boolean): Promise<ILayerProps>;
}

export class TextMaterial implements ITextMaterial {
    /**
     * 素材图层缓存
     * key: 素材 ID
     * value: 由原始素材图层解析后的客户端图层数据
     */
    private cache: Map<string, ILayerProps> = new Map();

    constructor(
        private framework: IFramework,
        private baseLayer: Layer,
    ) {}

    private parseTextMaterialToClient(materialID: string, serveLayer: IServerGroupLayerProps, version?: string) {
        const clonedServerLayer = cloneDeep(serveLayer);

        // 设置 materialIds
        const materialIDs = { ['advanced-text-material']: [materialID] };
        clonedServerLayer.materialIds = materialIDs;

        let clientLayer: ILayerProps;

        // 将服务端图层数据解析为客户端图层数据
        if (version) {
            const clientLayers = parseServerLayersToClient(
                { width: 100, height: 100, layers: [clonedServerLayer] }, // 宽高随意写，后续会重新设置
                version,
            );
            clientLayer = clientLayers[0];
        } else {
            clientLayer = parseServerToClientMap[clonedServerLayer.layerType]?.(clonedServerLayer);
        }

        // 重置图片图层 stretchOption
        traverseTree(clientLayer, (child: ILayerProps) => {
            if (child.layerType === LayerType.IMAGE_LAYER) {
                (child.data as IImageLayerProps).stretchOption = StretchOptionType.fill;
            }
        });

        if (clientLayer.layerType === LayerType.GROUP_LAYER) {
            (clientLayer.data as IGroupLayerProps).isTextMaterial = true;
        }

        return clientLayer;
    }

    public storeMaterialLayerToCache(materialID: string, serveLayer: IServerGroupLayerProps, version?: string) {
        const layer = this.parseTextMaterialToClient(materialID, serveLayer, version);
        this.cache.set(materialID, layer);

        return layer;
    }

    public loadMaterialLayerFromCache(materialID: string) {
        return cloneDeep(this.cache.get(materialID)) || null;
    }

    /**
     * 重置素材图层部分数据
     * ATTENTION: 该方法会改变入参可变数据
     * @param layer - 客户端图层数据
     */
    private resetLayer(layer: ILayerProps) {
        const reset = (layer: ILayerProps, parentID?: string) => {
            layer.id = uuid();

            if (layer.layerType === LayerType.TEXT_LAYER) {
                layer.__fixedWidth = true;

                // 执行 behaviors.onBeforeAddOrUpdate 钩子，即执行 server 端文本图层数据转为 client 端图层数据
                const behaviors = this.framework.service.behavior.get(layer);
                if (behaviors?.onBeforeAddOrUpdate) behaviors.onBeforeAddOrUpdate(layer);
            }

            if (parentID) {
                layer.parentId = parentID;
            }

            const children = layer.children || [];
            children.forEach((item) => {
                reset(item, layer.id);
            });
        };

        reset(layer);
    }

    private processFonts(layer: ILayerProps) {
        const fontService = this.framework.service.font;
        const defaultFontFamily = DEFAULT_FONT_FAMILY().name;

        traverseTrees([layer, ...layer.children], (child: ILayerProps) => {
            if (child.layerType === LayerType.TEXT_LAYER) {
                const layerData = child.data as ITextLayerProps;
                const { fontFamily, textContents = [] } = layerData;

                if (!fontService.getByFamily(fontFamily)) layerData.fontFamily = defaultFontFamily;

                if (textContents.length > 0) {
                    textContents.forEach((item) => {
                        if (!fontService.getByFamily(item.fontFamily)) item.fontFamily = defaultFontFamily;
                    });
                }
            }
        });
    }

    public async addByMaterialLayer(layer: ILayerProps, byMousePosition?: boolean) {
        const stageIdx = this.framework.service.frame.getActiveIndex();
        const { width: stageWidth, height: stageHeight } = this.framework.service.frame.getSizeByIndex(stageIdx);

        const prevLayerWidth = layer.width;
        const prevLayerHeight = layer.height;

        const widthRatio = prevLayerWidth / stageWidth;
        const heightRatio = prevLayerHeight / stageHeight;

        const scaleDistance = (() => {
            if (widthRatio > heightRatio) {
                return stageWidth / 2 - layer.width;
            }
            return layer.width / heightRatio / 2 - layer.width;
        })();

        let nextLayer = cloneDeep(layer);

        this.resetLayer(nextLayer); // 会改变 nextLayer
        this.processFonts(nextLayer); // 会改变 nextLayer
        this.framework.service.frame.scaleLayer(nextLayer, scaleDistance); // 会改变 nextLayer

        traverseTree(nextLayer, (child: ILayerProps) => {
            if (child.layerType === LayerType.VECTOR_LAYER) {
                const data = child.data as IVectorLayerProps;
                data.radius = data.radius * (nextLayer.width / prevLayerWidth);
            }
        });

        if (byMousePosition) {
            nextLayer = this.baseLayer.addByMousePosition({
                layer: nextLayer,
            });
        } else {
            nextLayer = this.baseLayer.addByDiagonal({
                layer: nextLayer,
            });
        }

        return nextLayer;
    }
}
